import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {Link} from 'react-router-dom';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import {
  DeleteInventoryStockItemsRequest,
  ErrorResponse, GetInventoryStockItemRequest,
  InventorySaleNumber,
  InventoryStock,
  InventoryStockItem,
  InventoryStockItemStateFilter, InventoryStockItemStateFilterRequest,
  PageResponseEnvelope,
  User,
  Warehouse
} from "src/api/dtos/dtos";
import * as tableUtils from "../utils/table-utils";
import {GridState, searchableSortableColumn, simpleColumn, sortableColumn} from "../utils/table-utils";
import locales from "../locales";
import {formatDateTimeOrNull} from "../utils/date-utils";
import {ColumnProps} from "antd/es/table";
import {Button, Icon, Switch, Table, Tooltip} from "antd";
import furoApi from "../api/FuroApi";
import {ErrorAlert} from "../common/ErrorAlert";
import * as paths from "../paths";
import InventoryStockItemsMoveModal from "./InventoryStockItemsMoveModal";
import InventoryStockItemsDeleteModal from "./InventoryStockItemsDeleteModal";
import {renderFixedPriceColumn} from "../utils/number-utils";
import InternalUsageModal from "./InternalUsageModal";
import {showDeleteConfirm} from "../utils/dialogs";
import InventoryStockItemStateSearch from "./InventoryStockItemStateSearch";
import accountProvider from '../account/AccountProvider';
import ButtonBar from "../common/ButtonBar";
import ButtonGroup from "antd/lib/button/button-group";
import InventoryStockItemEditModal from './InventoryStockItemEditModal';

interface LocationState {
  stateFilter?: InventoryStockItemStateFilter;
  showDeletedOnly?: boolean;
}

interface Props extends RouteComponentProps<{}, {}, LocationState> {
  inventoryStock: InventoryStock;
  infiniteStock: boolean;
}

interface State extends GridState<InventoryStockItem> {
  error?: ErrorResponse;
  showDeletedOnly: boolean;
  editModalVisible: boolean;
  editedItem?: InventoryStockItem;
  selectedItemIds: number[];
  bulkDeleteModalVisible: boolean;
  bulkMoveModalVisible: boolean;
  internalUsageModalVisible: boolean;
  stateFilter?: InventoryStockItemStateFilter;
  users: User[];
  warehouses: Warehouse[];
  saleNumbers: InventorySaleNumber[];
}

const FIELD_INVOICE_NUMBER = "invoiceNumber";
const FIELD_TRANSPORT_LETTER_NUMBER = "transportLetterNumber";
const FIELD_COST = "cost";
const FIELD_CREATED_AT = "createdAt";
const FIELD_LAST_CHANGED_AT = "lastChangedAt";
const FIELD_LAST_CHANGED_BY = "lastChangedBy";

class InventoryStockItemsEditor extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    ...tableUtils.initialGridState(),
    sortColumnKey: FIELD_CREATED_AT,
    sortOrder: 'descend',
    error: undefined,
    showDeletedOnly: this.props.location.state?.showDeletedOnly ?? false,
    editModalVisible: false,
    editedItem: undefined,
    selectedItemIds: [],
    bulkDeleteModalVisible: false,
    bulkMoveModalVisible: false,
    internalUsageModalVisible: false,
    stateFilter: this.props.location.state?.stateFilter,
    users: [],
    warehouses: [],
    saleNumbers: [],
  };

  public componentDidMount() {
    this.loadInventoryStockItems();
    this.loadUsers();
    this.loadWarehouses();
    this.loadSaleNumbers();
  }

  public render() {
    const {infiniteStock, inventoryStock} = this.props;
    const {
      data,
      error,
      showDeletedOnly,
      editModalVisible,
      editedItem,
      bulkMoveModalVisible,
      bulkDeleteModalVisible,
      selectedItemIds,
      users,
      warehouses,
      saleNumbers,
      internalUsageModalVisible,
      stateFilter,
    } = this.state;

    const columns: Array<ColumnProps<InventoryStockItem>> = [
      simpleColumn(locales.state, this.renderStateColumn),
      searchableSortableColumn(locales.incoming_invoice, FIELD_INVOICE_NUMBER, this.state, i => this.renderIncomingInvoiceLink(i)),
      searchableSortableColumn(locales.transport_letter, FIELD_TRANSPORT_LETTER_NUMBER, this.state, i => this.renderTransportLetterLink(i), 140),
      sortableColumn(locales.initial_cost, FIELD_COST, this.state, this.renderCost),
      simpleColumn(locales.profit_margin_percent, i => i.profitMargin != null ? `${i.profitMargin}%` : '-'),
      simpleColumn(locales.selling_price, this.renderSellingPrice),
    ];

    if (showDeletedOnly) {
      columns.push(...[
        simpleColumn(locales.delete_date, (i: InventoryStockItem) => formatDateTimeOrNull(i.deletedAt) || '-'),
        simpleColumn(locales.delete_reason, (i: InventoryStockItem) => i.deletedReason || '-')
      ]);
    } else {
      columns.push(sortableColumn(locales.created_at, FIELD_CREATED_AT, this.state, i => formatDateTimeOrNull(i.createdAt) || '-'));
      if (!infiniteStock) {
        columns.push(sortableColumn(locales.last_changed_at, FIELD_LAST_CHANGED_AT, this.state, i => formatDateTimeOrNull(i.lastChangedAt) || '-'))
      }
      columns.push(sortableColumn(locales.last_changed_by, FIELD_LAST_CHANGED_BY, this.state, i => i.lastChangedBy || '-'))
      columns.push({key: 'actions', width: 80, render: this.renderActionColumn,})
    }

    const rowSelection = {
      selectedRowKeys: this.state.selectedItemIds,
      onChange: (selectedRowKeys: Array<string | number>) => {
        this.setState({selectedItemIds: selectedRowKeys as number[]});
      },
      getCheckboxProps: (record: InventoryStockItem) => ({
        disabled: !(record.id || record.inventoryStock.id) || !!record.deletedAt || !(record.state?.warehouseName || record.state?.userName || record.state?.internallyUsed),
      }),
    };

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <InventoryStockItemStateSearch
          users={users}
          saleNumbers={saleNumbers}
          warehouses={warehouses}
          stateFilter={stateFilter}
          onOk={this.onStateFilterApply}
          onClear={this.onStateFilterClear}
        />

        {!infiniteStock &&
          this.renderNonInfiniteButtons()}

        {infiniteStock &&
          this.renderInfiniteButtons()}


        <Table
          style={{marginBottom: 24}}
          columns={columns}
          rowKey={this.getRowKey}
          rowSelection={rowSelection}
          dataSource={data}
          bordered={true}
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          size="small"
          scroll={{x: 1024}}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
        />

        {bulkDeleteModalVisible &&
            <InventoryStockItemsDeleteModal
                selectedItemIds={selectedItemIds}
                onCancel={this.onBulkDeleteCancel}
                onConfirm={this.onBulkDeleteConfirm}
            />
        }

        {editModalVisible && editedItem &&
            <InventoryStockItemEditModal
                item={editedItem}
                onCancel={this.onEditCancel}
                onConfirm={this.onEditConfirm}
            />
        }

        {bulkMoveModalVisible &&
            <InventoryStockItemsMoveModal
                selectedItemIds={selectedItemIds}
                onCancel={this.onBulkMoveCancel}
                onConfirm={this.onBulkMoveConfirm}
                users={users}
                warehouses={warehouses}
            />
        }

        {internalUsageModalVisible &&
            <InternalUsageModal
                inventoryStock={inventoryStock}
                onCancel={this.onInternalUsageCancel}
                onConfirm={this.onInternalUsageConfirm}
            />
        }


        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    )
      ;
  };

  private getRowKey = (item: InventoryStockItem, index?: number): string => {
    const key = item.id || item.inventoryStock.id || index || 0;
    return key.toString();
  }

  private renderNonInfiniteButtons = (): React.ReactNode => {
    const {showDeletedOnly} = this.state;
    return <ButtonBar style={{gap: 8, marginBottom: 8}}>
      <span style={{marginRight: 4}}>{locales.show_only_deleted}</span>
      <Switch checked={showDeletedOnly} onChange={this.onShowDeletedOnlyChange}/>

      <Button
        size="small"
        type="danger"
        icon="delete"
        disabled={this.state.selectedItemIds.length === 0}
        onClick={this.onBulkDeleteClick}
      >
        {locales.delete}
      </Button>

      <Button
        size="small"
        type="default"
        icon="swap"
        disabled={this.state.selectedItemIds.length === 0}
        onClick={this.onBulkMoveClick}
      >
        {locales.move}
      </Button>

      <Button
        size="small"
        type="primary"
        icon="plus"
        onClick={this.onAddButtonClick}
      >
        {locales.add}
      </Button>
    </ButtonBar>;
  }

  private renderInfiniteButtons = (): React.ReactNode => {
    return <ButtonBar style={{gap: 8, marginBottom: 8}}>
      <Button
        size="small"
        type="danger"
        icon="delete"
        disabled={this.state.selectedItemIds.length === 0}
        onClick={this.onBulkDeleteClick}
      >
        {locales.delete}
      </Button>
      <Button
        size="small"
        type="primary"
        icon="plus"
        onClick={this.onAddInternallyUsedClick}
      >
        {locales.internally_used_add}
      </Button>
    </ButtonBar>;
  }


  private renderIncomingInvoiceLink = (item: InventoryStockItem): React.ReactNode => {
    const invoice = item.incomingInvoice;

    if (!invoice) {
      return '-';
    }

    return (
      <>
        <Link to={paths.viewIncomingInvoicePath(invoice.id!)}>
          {invoice.invoiceNumber}
        </Link>
        <br/>
        {invoice.name && (
          <span style={{fontSize: '12px'}}>
              {invoice.name}
          </span>
        )}
      </>
    );
  };

  private renderTransportLetterLink = (item: InventoryStockItem): React.ReactNode => {
    const transportLetter = item.inventoryTransportLetter;

    if (!transportLetter) {
      return;
    }

    return (
      <Link to={paths.viewInventoryTransportLetterPath(transportLetter.id!)}>
        {transportLetter.transportLetterNumber}
      </Link>

    );
  };

  private renderStateColumn = (item: InventoryStockItem): React.ReactNode => {
    const state = item.state;
    if (!state) {
      return '-';
    }

    if (state.warehouseName) {
      return (
        <span>
          <Tooltip title={locales.warehouse}>
            <Icon type="shop" style={{marginRight: 6}}/>
          </Tooltip>
          {state.warehouseName}
        </span>
      );
    }

    if (state.userName) {
      return (
        <span>
          <Tooltip title={locales.user}>
            <Icon type="user" style={{marginRight: 6}}/>
          </Tooltip>
          {state.userName}
        </span>
      );
    }

    if (state.maintenanceHistoryId && state.maintenanceItemName) {
      return (
        <>
          <Link to={paths.viewMaintenanceHistoryItemPath(state.maintenanceHistoryId)}>
            <Tooltip title={locales.maintenance_worksheet}>
              <Icon type="snippets" style={{marginRight: 6}}/>
            </Tooltip>
            {state.maintenanceItemName}
          </Link>
          <br/>
          {state.itemId && state.itemName && (
            <Link to={paths.viewItemPath(state.itemId)} style={{fontSize: '12px'}}>
              <Tooltip title={locales.maintenance_worksheet}>
                <Icon type="settings" style={{marginRight: 6}}/>
              </Tooltip>
              {state.itemName}
            </Link>
          )}
        </>
      );
    }

    if (state.internallyUsed) {
      return (
        <>
          <span>
            {locales.internally_used}
          </span>
          <br/>
          {state.itemId && state.itemName && (
            <Link to={paths.viewItemPath(state.itemId)} style={{fontSize: '12px'}}>
              {state.itemName}
            </Link>
          )}
        </>
      )
    }

    if (state.inventorySaleId != null) {
      return (
        <>
      <span>
        {locales.sold}
        </span>
          <br/>
          {state.inventorySaleNumber && (
            accountProvider.isAdmin() ? (
              <Link to={paths.viewSalePath(state.inventorySaleId)} style={{fontSize: '12px'}}>
                {state.inventorySaleNumber}
              </Link>
            ) : state.inventorySaleNumber
          )}
        </>
      )
    }

    return '-';
  };

  private renderCost = (item: InventoryStockItem): React.ReactNode => {
    return renderFixedPriceColumn(item.hufCost);
  };

  private renderSellingPrice = (item: InventoryStockItem): React.ReactNode => {
    return renderFixedPriceColumn((1 + item.profitMargin / 100.0) * item.hufCost);
  };

  private renderActionColumn = (_: any, item: InventoryStockItem, __: number) => {
    const onEditItemClick = () => this.onEditClick(item);

    if (item.state?.warehouseName || item.state?.userName) {
      return (
        <div className="action-column" style={{textAlign: 'center'}}>
          <ButtonGroup>
            <Button icon="edit" onClick={onEditItemClick} title={locales.edit}/>
          </ButtonGroup>
        </div>
      )
        ;
    }

    return '';
  };


  private onEditClick = (item: InventoryStockItem) => {
    if (!item.id) {
      return;
    }
    this.setState({
      editModalVisible: true,
      editedItem: item,
    });
  };

  private onEditCancel = () => {
    this.setState({
      editModalVisible: false,
      editedItem: undefined,
    });
  };

  private onEditConfirm = () => {
    this.setState({
      editModalVisible: false,
      editedItem: undefined,
    }, () => this.loadInventoryStockItems());
  };


  private onShowDeletedOnlyChange = (checked: boolean) => {
    this.props.history.replace({
      ...this.props.location,
      state: {...this.props.location.state, showDeletedOnly: checked},
    });
    this.setState({showDeletedOnly: checked, selectedItemIds: []}, () =>
      this.loadInventoryStockItems());
  };

  private onBulkMoveClick = () => {
    this.setState({bulkMoveModalVisible: true});
  };

  private onBulkMoveCancel = () => {
    this.setState({bulkMoveModalVisible: false})
  };

  private onBulkMoveConfirm = () => {
    this.setState({
      selectedItemIds: [],
      bulkMoveModalVisible: false,
    });
    this.loadInventoryStockItems();
  }

  private onBulkDeleteClick = () => {
    const {infiniteStock} = this.props;
    const {selectedItemIds} = this.state;

    if (infiniteStock) {
      showDeleteConfirm(() => {
          const request: DeleteInventoryStockItemsRequest = {
            ids: selectedItemIds,
          };

          const promise = furoApi.inventoryStockItems.delete(request)
            .then(this.onBulkDeleteConfirm)
            .catch(this.onApiError);

          withLoadingOverlay(promise, this.loadingHandler);
        },
        locales.delete_confirm_multiple,
      );
    } else {
      this.setState({bulkDeleteModalVisible: true});
    }
  };

  private onBulkDeleteCancel = () => {
    this.setState({bulkDeleteModalVisible: false});
  };

  private onBulkDeleteConfirm = () => {
    this.setState({
      selectedItemIds: [],
      bulkDeleteModalVisible: false,
    });
    this.loadInventoryStockItems();
  }

  private onAddButtonClick = () => {
    const {inventoryStock} = this.props;
    this.props.history.push(paths.createInventoryStockItemPath(inventoryStock.id!));
  };

  private onAddInternallyUsedClick = () => {
    this.setState({internalUsageModalVisible: true});
  };

  private onInternalUsageCancel = () => {
    this.setState({internalUsageModalVisible: false});
  };

  private onInternalUsageConfirm = () => {
    this.setState({internalUsageModalVisible: false});
    this.loadInventoryStockItems();
  };


  private onStateFilterApply = (stateFilter: InventoryStockItemStateFilter) => {
    this.props.history.replace({
      ...this.props.location,
      state: {...this.props.location.state, stateFilter},
    });
    this.setState({
      page: 1,
      stateFilter,
    }, () => this.loadInventoryStockItems());
  };

  private onStateFilterClear = () => {
    this.props.history.replace({
      ...this.props.location,
      state: {...this.props.location.state, stateFilter: undefined},
    });
    this.setState({
      stateFilter: undefined,
      page: 1
    }, () => this.loadInventoryStockItems());
  };

  private loadInventoryStockItems = () => {
    const {stateFilter} = this.state;

    const filter: InventoryStockItemStateFilterRequest | undefined = stateFilter ? {
      targetType: stateFilter.targetType,
      userId: stateFilter.userId,
      warehouseId: stateFilter.warehouseId,
      itemId: stateFilter.item?.id,
      saleId: stateFilter.saleId,
    } : undefined;

    const request: GetInventoryStockItemRequest = {
      ...tableUtils.toGridRequest(this.state),
      deleted: this.state.showDeletedOnly,
      inventoryStockId: this.props.inventoryStock.id!,
      stateFilter: filter,
    };

    const promise = furoApi.inventoryStockItems.getAll(request)
      .then((response: PageResponseEnvelope<InventoryStockItem>) => {
        const newState = {
          ...tableUtils.toGridState(response),
          error: undefined,
        };
        this.setState(newState);
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then((users) => this.setState({
        users: users
          .filter(user => user.enabled && user.userName)
      }))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadWarehouses = () => {
    const promise = furoApi.warehouses.getWarehouses()
      .then((warehouses) => this.setState({warehouses}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadSaleNumbers = () => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    const promise = furoApi.sales.getAllSaleNumbers()
      .then((saleNumbers) => this.setState({saleNumbers}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onApiError = (error: ErrorResponse) => this.setState({
    error
  });

  private onGridChange = (newState: GridState<InventoryStockItem>) => {
    this.setState({...newState}, () => this.loadInventoryStockItems());
  };
}

export default withRouter(InventoryStockItemsEditor);



// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockItemsEditor.tsx